import { useMemo } from 'react';

import { User } from '~/constants/graphqlTypes';
import { useSessionContext } from '~/contexts/SessionContext';
import { Permission, Scope } from '~/types/api.graphql';

export class UserPermissionsManager {
  private user: User;
  private isOrgAdmin: boolean;

  constructor(user: User) {
    this.user = user;
    this.isOrgAdmin = user?.scopes?.includes(Scope.OrgsAdmin) || false;
  }

  private isPermissionExistInOrgs(permission: Permission) {
    const isRequestedPermission = (p: Permission) => [permission, Permission.SuperOwner].includes(p);
    return this.user.userRoles.some(
      (userRole) =>
        userRole.assignedRole.permissions.some(isRequestedPermission) ||
        userRole.assignedRole.childOrgExtraPermissions.some(isRequestedPermission),
    );
  }

  private hasPermissionTo(permission: Permission) {
    if (!this.user) return false;
    return this.isOrgAdmin || this.isPermissionExistInOrgs(permission);
  }

  public canCreateOrganization() {
    return this.hasPermissionTo(Permission.CreateOrganization);
  }

  public canCreateAudience() {
    return this.hasPermissionTo(Permission.CreateAudience);
  }

  public canCreateSegment() {
    return this.hasPermissionTo(Permission.CreateAudienceSegment);
  }

  // Whether any organization lets the user create Matchbacks; the exact check runs per organization.
  // No OrgsAdmin shortcut: like OrganizationPermissionsManager and the API, only AudiencesAdmin or the role grants it.
  public canCreateMatchback() {
    if (!this.user) return false;
    return (
      !!this.user.scopes?.includes(Scope.AudiencesAdmin) ||
      this.isPermissionExistInOrgs(Permission.CreateAudienceMatchback)
    );
  }

  public canReadOrganization() {
    return this.hasPermissionTo(Permission.ReadOrganization);
  }
}

// Receives audience data to check if the user is invited to the audience
// This is in the flow that the user is not part of the organization
// but is used in the flow that the user is invited to the audience
export const useUserPermissions = () => {
  const { me } = useSessionContext();

  const permissionsManager = useMemo(() => new UserPermissionsManager(me), [me]);
  return { userPermissionsManager: permissionsManager };
};
